import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Color: Dialysis Diet Pro primary teal
const PRIMARY = [40, 89, 107];
const MUTED = [75, 85, 99];
const DANGER = [220, 38, 38];
const GUIDANCE_DISCLAIMER = "This app is for guidance purposes only. Always consult your medical team.";

function header(doc, title, subtitle) {
  // Brand bar
  doc.setFillColor(...PRIMARY);
  doc.rect(0, 0, doc.internal.pageSize.getWidth(), 28, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("Dialysis Diet Pro", 14, 17);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text("Dialysis Nutrition Tracker", 14, 23);

  doc.setTextColor(17, 24, 39);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(title, 14, 42);
  if (subtitle) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...MUTED);
    doc.text(subtitle, 14, 48);
  }
  doc.setTextColor(17, 24, 39);
}

function footer(doc) {
  const h = doc.internal.pageSize.getHeight();
  doc.setFontSize(8);
  doc.setTextColor(...MUTED);
  doc.text(
    GUIDANCE_DISCLAIMER,
    14,
    h - 10,
  );
  doc.text(`Generated ${new Date().toLocaleString()}`, doc.internal.pageSize.getWidth() - 14, h - 10, { align: "right" });
}

function formatWeight(weightKg) {
  return weightKg ? `${weightKg} kg` : "- kg";
}

export function exportDailyPdf(summary, userName, weightKg) {
  const doc = new jsPDF();
  header(
    doc,
    `Daily Harmful Nutrients Report - ${summary.date}`,
    `Patient: ${userName || "-"} - Weight: ${formatWeight(weightKg)}`,
  );

  const limits = summary.limits;
  const totals = summary.totals;
  const rows = [
    ["Potassium", `${totals.potassium} mg`, `${limits.potassium} mg`, totals.potassium <= limits.potassium ? "Safe" : "Exceeded"],
    ["Phosphorus", `${totals.phosphorus} mg`, `${limits.phosphorus} mg`, totals.phosphorus <= limits.phosphorus ? "Safe" : "Exceeded"],
    ["Sodium", `${totals.sodium} mg`, `${limits.sodium} mg`, totals.sodium <= limits.sodium ? "Safe" : "Exceeded"],
    ["Protein", `${totals.protein} g`, `${limits.protein} g`, totals.protein <= limits.protein ? "Safe" : "Exceeded"],
  ];

  autoTable(doc, {
    startY: 56,
    head: [["Nutrient", "Consumed", "Limit", "Status"]],
    body: rows,
    headStyles: { fillColor: PRIMARY, textColor: 255 },
    styles: { fontSize: 10, cellPadding: 4 },
    alternateRowStyles: { fillColor: [249, 250, 251] },
    didParseCell: (data) => {
      if (data.section === "body" && data.column.index === 3 && data.cell.raw === "Exceeded") {
        data.cell.styles.textColor = DANGER;
      }
    },
  });

  const entryRows = (summary.entries || []).map((e) => [
    e.food_name,
    e.serving_size || "",
    e.quantity,
    `${e.potassium}`,
    `${e.phosphorus}`,
    `${e.sodium}`,
    `${e.protein}`,
  ]);

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 10,
    head: [["Food", "Serving Size", "Quantity", "K (mg)", "P (mg)", "Na (mg)", "Protein (g)"]],
    body: entryRows.length ? entryRows : [["No entries", "", "", "", "", "", ""]],
    headStyles: { fillColor: PRIMARY, textColor: 255 },
    styles: { fontSize: 9, cellPadding: 3 },
    alternateRowStyles: { fillColor: [249, 250, 251] },
  });

  footer(doc);
  doc.save(`Dialysis Diet Pro-${summary.date}.pdf`);
}

export function exportHistoryPdf(days, userName, weightKg) {
  const doc = new jsPDF();
  const first = days[0]?.date || "";
  const last = days[days.length - 1]?.date || "";
  header(
    doc,
    "28-Day Harmful Nutrients Report",
    `Patient: ${userName || "-"} - Weight: ${formatWeight(weightKg)} - ${first} to ${last}`,
  );

  const rows = days.map((d) => [
    d.date,
    d.totals.potassium,
    d.totals.phosphorus,
    d.totals.sodium,
    d.totals.protein,
    d.entries?.length || 0,
  ]);

  autoTable(doc, {
    startY: 56,
    head: [["Date", "K (mg)", "P (mg)", "Na (mg)", "Protein (g)", "Entries"]],
    body: rows,
    headStyles: { fillColor: PRIMARY, textColor: 255 },
    styles: { fontSize: 10, cellPadding: 4 },
    alternateRowStyles: { fillColor: [249, 250, 251] },
  });

  footer(doc);
  doc.save(`Dialysis Diet Pro-28day-${last}.pdf`);
}
